import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Plus, 
  Clock, 
  Video, 
  MapPin, 
  CreditCard,
  CalendarIcon,
  ChevronRight,
  Filter,
  FlaskConical
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

const SessionsPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    patient_id: '',
    scheduled_date: new Date(),
    scheduled_time: '',
    mode: 'video',
    theme: '',
    fee: ''
  });

  const realPatients = useMemo(() => patients.filter(p => !p.is_demo), [patients]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter, paymentFilter]);

  const fetchData = async () => {
    try {
      let url = `${API}/sessions`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (paymentFilter !== 'all') params.append('payment_status', paymentFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const [sessionsRes, patientsRes] = await Promise.all([
        fetch(url, { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API}/patients`, { headers: getAuthHeaders(), credentials: 'include' })
      ]);

      if (sessionsRes.ok) {
        setSessions(await sessionsRes.json());
      }
      if (patientsRes.ok) {
        setPatients(await patientsRes.json());
      }
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_id || !formData.scheduled_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate session is not in the past
    const sessionDateTime = new Date(formData.scheduled_date);
    const [hours, minutes] = formData.scheduled_time.split(':').map(Number);
    sessionDateTime.setHours(hours, minutes, 0, 0);
    
    if (sessionDateTime < new Date()) {
      toast.error('Cannot schedule sessions in the past');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/sessions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          scheduled_date: formData.scheduled_date.toISOString(),
          fee: formData.fee ? parseFloat(formData.fee) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create session');
      }

      const newSession = await response.json();
      setSessions(prev => [newSession, ...prev]);
      setDialogOpen(false);
      setFormData({
        patient_id: '',
        scheduled_date: new Date(),
        scheduled_time: '',
        mode: 'video',
        theme: '',
        fee: ''
      });
      toast.success('Session created successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="sessions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-foreground"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Sessions
            </h1>
            <p className="text-muted-foreground mt-1">
              {sessions.length} total sessions
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-primary hover:bg-primary/90" data-testid="new-session-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Session
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>
                  Schedule New Session
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Patient *</Label>
                  <Select
                    value={formData.patient_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, patient_id: value }))}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="patient-select">
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {realPatients.map((patient) => (
                        <SelectItem key={patient.patient_id} value={patient.patient_id}>
                          {patient.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal rounded-xl",
                            !formData.scheduled_date && "text-muted-foreground"
                          )}
                          data-testid="date-picker-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.scheduled_date ? format(formData.scheduled_date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.scheduled_date}
                          onSelect={(date) => setFormData(prev => ({ ...prev, scheduled_date: date }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label>Time *</Label>
                    <Input
                      type="time"
                      value={formData.scheduled_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                      className="rounded-xl"
                      required
                      data-testid="time-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mode *</Label>
                    <Select
                      value={formData.mode}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, mode: value }))}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="video">
                          <div className="flex items-center gap-2">
                            <Video className="h-4 w-4" />
                            Video Call
                          </div>
                        </SelectItem>
                        <SelectItem value="in-person">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            In-Person
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Fee (₹) *</Label>
                    <Input
                      type="number"
                      value={formData.fee}
                      onChange={(e) => setFormData(prev => ({ ...prev, fee: e.target.value }))}
                      className="rounded-xl"
                      placeholder="1500"
                      required
                      data-testid="fee-input"
                    />
                    <p className="text-xs text-muted-foreground">Required for invoice generation</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Session Theme/Focus</Label>
                  <Input
                    value={formData.theme}
                    onChange={(e) => setFormData(prev => ({ ...prev, theme: e.target.value }))}
                    className="rounded-xl"
                    placeholder="e.g., Anxiety management, Relationship issues"
                    data-testid="theme-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-xl bg-primary hover:bg-primary/90"
                    disabled={submitting}
                    data-testid="create-session-btn"
                  >
                    {submitting ? 'Creating...' : 'Create Session'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="status-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="tentative">Tentative</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <Select value={paymentFilter} onValueChange={setPaymentFilter}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="payment-filter">
              <CreditCard className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Payment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* CRM Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-primary">Loading sessions...</div>
          </div>
        ) : sessions.length === 0 ? (
          <Card className="border-border">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CalendarIcon className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No sessions found</h3>
              <p className="text-muted-foreground text-center max-w-sm">
                {statusFilter !== 'all' || paymentFilter !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Schedule your first session to get started'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[200px]">Patient</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Theme/Focus</TableHead>
                    <TableHead className="w-[100px]">Mode</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[100px]">Payment</TableHead>
                    <TableHead className="w-[80px]">Fee</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow 
                      key={session.session_id}
                      className={`cursor-pointer hover:bg-muted/50 ${session.is_demo ? 'bg-amber-50/30' : ''}`}
                      onClick={() => navigate(`/sessions/${session.session_id}`)}
                      data-testid={`session-row-${session.session_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${session.is_demo ? 'bg-amber-100' : 'bg-secondary/30'}`}>
                            <span className={`text-xs font-medium ${session.is_demo ? 'text-amber-700' : 'text-primary'}`}>
                              {session.patient_name?.charAt(0) || 'P'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">{session.patient_name}</span>
                            {session.is_demo && (
                              <FlaskConical className="h-3 w-3 text-amber-600" />
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{format(new Date(session.scheduled_date), 'MMM d, yyyy')}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {session.scheduled_time}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {session.theme || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm">
                          {session.mode === 'video' ? (
                            <Video className="h-3 w-3 text-blue-600" />
                          ) : (
                            <MapPin className="h-3 w-3 text-green-600" />
                          )}
                          <span className="capitalize">{session.mode}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={`text-xs ${session.status === 'confirmed' ? 'badge-confirmed' : session.status === 'completed' ? 'badge-paid' : 'badge-tentative'}`}
                        >
                          {session.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={`text-xs ${session.payment_status === 'paid' ? 'badge-paid' : 'badge-pending'}`}
                        >
                          {session.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {session.fee ? `₹${session.fee}` : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default SessionsPage;
